import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { DepartmentApiObject, DepartmentsApiResponse } from '../stockTransfer.helper';
import { useDepartmentList } from '../../Queries/UseStockTransferQueries';
import { useStockTransferStore } from '../../Store/useStockTransferstore';

const FilterModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { t: tStockTransfer } = useTranslation('stockTransfer_config');
  const [departmentList, setDepartmentList] = useState<DepartmentApiObject[]>([]);
  const stockTransferStore = useStockTransferStore(); 
  const { data: DepartmentList, error: DepartmentListError } = useDepartmentList();

  useEffect(() => {
    const list: DepartmentApiObject[] = (DepartmentList as DepartmentsApiResponse | undefined)?.objects || [];
    setDepartmentList(list);
  }, [DepartmentList,DepartmentListError]);

  const clearFilters = () => {
    stockTransferStore.setFromClinicId(null);
    stockTransferStore.setFromStoreId(null);
    stockTransferStore.setToClinicId(null);
    stockTransferStore.setToStoreId(null);
    stockTransferStore.setFromDate(null);
    stockTransferStore.setToDate(null);
    // Immediately trigger apply to refetch with no filters
    stockTransferStore.triggerApplyFilters();
  };

  const handleClose = () => {
    clearFilters();
    onClose();
  };

  // Determine if any filter is selected to control button states
  const hasAnyFilterSelected = Boolean(
    stockTransferStore.fromStoreId ||
    stockTransferStore.toStoreId ||
    stockTransferStore.fromDate ||
    stockTransferStore.toDate
  );


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 1,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontSize: '24px',
          fontWeight: 700,
          px: 2,
          pt: 2,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {tStockTransfer('Filters')}
        <IconButton onClick={handleClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2 }}>
        {/* Store Section */}
        <Typography fontWeight={600} mb={1} mt={2}>
          {tStockTransfer('Store')}
        </Typography>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={departmentList}
              getOptionLabel={(opt: DepartmentApiObject) => `${opt.clinic__name || ''} - ${opt.name_label || opt.name || ''}`}
              value={departmentList.find(o => o.id === stockTransferStore.fromStoreId) || null}
              onChange={(_, val: DepartmentApiObject | null) => {
                stockTransferStore.setFromClinicId(val ? (val.clinic__id as number) : null);
                stockTransferStore.setFromStoreId(val ? (val.id as number) : null);
              }}
              sx={{ minWidth: 140 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tStockTransfer('From Clinic | Store')}
                  size="small"
                  sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { height: 44 }, borderRadius: 3 }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={departmentList}
              getOptionLabel={(opt: DepartmentApiObject) => `${opt.clinic__name || ''} - ${opt.name_label || opt.name || ''}`}
              value={departmentList.find(o => o.id === stockTransferStore.toStoreId) || null}
              onChange={(_, val: DepartmentApiObject | null) => {
                stockTransferStore.setToClinicId(val ? (val.clinic__id as number) : null);
                stockTransferStore.setToStoreId(val ? (val.id as number) : null);
              }}
              sx={{ minWidth: 140 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tStockTransfer('To Clinic | Store')}
                  size="small"
                  sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { height: 44 } }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Indent Date Section */}
        <Typography fontWeight={600} mb={1}>
          {tStockTransfer('Date')}
        </Typography>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={tStockTransfer('From Date')}
              size="small"
              InputLabelProps={{ shrink: true }}
              value={stockTransferStore.fromDate ?? ''}
              onChange={(e) => {
                const newFrom = e.target.value || null;
                stockTransferStore.setFromDate(newFrom);
                const to = stockTransferStore.toDate || null;
                if (!newFrom) {
                  stockTransferStore.setToDate(null);
                } else if (to && to < newFrom) {
                  stockTransferStore.setToDate(null);
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { height: 44 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={tStockTransfer('To Date')}
              size="small"
              InputLabelProps={{ shrink: true }}
              value={stockTransferStore.toDate ?? ''}
              onChange={(e) => {
                const val = e.target.value || null;
                const from = stockTransferStore.fromDate || null;
                if (val && from && val < from) {
                  return;
                }
                stockTransferStore.setToDate(val);
              }}
              inputProps={{
                min: stockTransferStore.fromDate ?? undefined,
              }}
              disabled={!stockTransferStore.fromDate}
              sx={{ '& .MuiOutlinedInput-root': { height: 44 } }}
            />
          </Grid>
        </Grid>

        {/* Footer Buttons */}
        <Box display="flex" justifyContent="space-between" gap={2} mb={2}>
          <Button
            fullWidth
            variant="outlined"
            sx={{
              height: 50,
              fontWeight: 600,
              borderRadius: 2,
            }}
            onClick={clearFilters}
            disabled={!hasAnyFilterSelected}
          >
            {tStockTransfer('Clear')}
          </Button>

          <Button
            fullWidth
            variant="contained"
            sx={{
              height: 50,
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: '#4A4A4A',
              '&:hover': { backgroundColor: '#3a3a3a' },
            }}
            onClick={() => {
              stockTransferStore.triggerApplyFilters();             
              onClose();
            }}
            disabled={!hasAnyFilterSelected}
          >
            {tStockTransfer('Apply')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default FilterModal;    